// NETWORKING layer
// Customizing the request before and after it's sent
// You can define middleware/afterware, and plug them in GraphQL
// https://www.apollographql.com/docs/react/v2.6/networking/network-layer/

import { message } from "antd";
import {
  HttpLink,
  ApolloLink,
  concat,
  ServerError,
  ServerParseError,
  ApolloClient,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { InMemoryCache } from "@apollo/client/cache";

import { logout } from "utils/auth";
import history from "utils/history";
import i18next from "i18next";

// for Graphql mock
import { typeDefs, resolvers } from "mock_data/MockData";

import { languageDataVar } from "cache";
import { getCredentials } from "./auth";
import { showRateLimitError } from "./networkErrors";

// ---------------
// MIDDLEWARE
// Middlewares are used to inspect and modify every request made over the link,
// for example, adding authentication tokens to every query.
// In order to add middleware, you simply create a new link and join it with the HttpLink
// --------------
const httpLink = new HttpLink({
  uri: `${process.env.REACT_APP_API_URL}/graphql`,
});

// add the authorization to the headers
const authMiddleware = new ApolloLink((operation, forward) => {
  const auth = getCredentials();

  operation.setContext(({ headers = {} }: any) => ({
    headers: {
      ...headers,
      ...auth,
      "token-type": "Bearer",
    },
  }));

  return forward(operation);
});

// -------------
// AFTERWARE
// 'Afterware' is very similar to a middleware,
// except that an afterware runs after a request has been made,
// that is when a response is going to get processed.
// --------------
const errorHandling = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(
      ({ message: description, locations, path, extensions }) => {
        console.log(
          `[GraphQL error]: Message: ${description}, Location: ${locations}, Path: ${path}`
        );
        switch (extensions?.code) {
          case "UNAUTHENTICATED":
          case "AUTHENTICATION_ERROR":
            if (window.location.href.indexOf("/login") < 0) {
              console.error({
                message: "Authentication",
                description,
              });
              logout();
              message.success(
                i18next.t("You have been logged out. Please log in again")
              );
              history.push(`/login`);
            }
            break;
          case "INTERNAL_SERVER_ERROR":
            if (window.location.href.indexOf("/error") < 0) {
              history.push("/error");
            }
            break;
          default:
            break;
        }
      }
    );

  if (networkError) {
    console.log(`[Network error]: ${networkError}`);

    // Read about list of error types here
    // https://www.apollographql.com/docs/link/links/http/#errors
    const error = networkError as ServerError | ServerParseError;

    if (error.statusCode) {
      switch (error.statusCode) {
        case 429: // Too many requests
          // Cloudflare returns an HTML response for rate limits, which Apollo
          // reports as a parse error. Use a stable message key so simultaneous
          // failed queries update one message instead of stacking many of them.
          showRateLimitError();
          break;
        case 500: // Internal server error
          // Only redirect if current path is not /error
          if (window.location.href.indexOf("/error") < 0) {
            window.location.href = "/error";
          }
          break;
        case 404:
          message.error("Your request is invalid.");
          break;
        case 400: // Bad requests
          // mostly when user submit a form
          // TODO: global form error handling if needed
          break;
        case 401: // Unauthorized
          // TODO: forward user to login form, remember the target path
          // so you can redirect the user after login successfully
          break;
        case 403: // Forbidden
          break;
        default:
          message.error(
            "An error has occurred with the network. Try to refresh the page or close your browser and log in again."
          );
      }
    } else {
      // normal Error
      message.error(
        "There seems to be a problem with your network. Please check it and retry."
      );
    }
  }
});

// -------------------------------------
// Bundling middleware/afterware all together
let links = httpLink as ApolloLink;

// Middlewares
links = concat(authMiddleware, links);

// Afterwares
links = concat(errorHandling, links);

// -------
// Initialise new Apollo client
// -------
const client = new ApolloClient({
  connectToDevTools:
    process.env.NODE_ENV === "development" ||
    process.env.REACT_APP_API_URL?.includes("coderpush.com"),
  link: links,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          languageDataCache: {
            read() {
              return languageDataVar();
            },
          },
        },
      },
    },
  }),
  typeDefs,
  resolvers,
  defaultOptions: {
    query: {
      errorPolicy: "all",
    },
  },
});

export default client;
